import { useRef, useState } from "react";
import { uploadFile, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Replace, Trash2, Maximize2, FileText, Loader2, AlertTriangle } from "lucide-react";

export default function FileUpload({
  label,
  value,
  onChange,
  accept = ".jpg,.jpeg,.png,.webp",
  purpose = "generic",
  minWidth = 0,
  minHeight = 0,
  aspectHint,
  aspectLabel = "portrait 4:5",
  aspectAdvice = "",
  testid = "file-upload",
  className = "",
  heightClass = "h-52",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [full, setFull] = useState(false);

  const isPdf = value?.content_type === "application/pdf" || value?.original_filename?.toLowerCase().endsWith(".pdf");
  const url = fileUrl(value?.url);

  const lowRes = value?.width && minWidth && value.width < minWidth;
  const ratio = value?.width && value?.height ? value.width / value.height : null;
  const badAspect = aspectHint && ratio && Math.abs(ratio - aspectHint) / aspectHint > 0.12;

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File exceeds the 20 MB limit");
      return;
    }
    setBusy(true);
    try {
      const data = await uploadFile(file, purpose);
      onChange(data);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className} data-testid={testid}>
      {label && <div className="mb-2 text-sm font-semibold text-[#14513D]">{label}</div>}
      <input ref={inputRef} type="file" accept={accept} onChange={onFile} className="hidden" data-testid={`${testid}-input`} />

      {!value ? (
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          data-testid={`${testid}-dropzone`}
          className={`w-full ${heightClass} rounded-xl border-2 border-dashed border-[#C9A34A]/50 bg-[#FAF7F0] hover:border-[#C9A34A] hover:bg-[#C9A34A]/5 transition-colors flex flex-col items-center justify-center gap-2 text-[#14513D]`}
        >
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
          <span className="text-sm font-medium">{busy ? "Uploading…" : "Click to upload"}</span>
          <span className="text-xs text-[#14513D]/60">{accept.replaceAll(".", "").toUpperCase()} · max 20 MB</span>
          {aspectHint && <span className="text-[11px] text-[#14513D]/50" data-testid={`${testid}-aspect-hint`}>Recommended {aspectLabel}</span>}
        </button>
      ) : (
        <div className="rounded-xl border border-[#C9A34A]/30 bg-[#FAF7F0] overflow-hidden">
          <div className={`relative ${heightClass} bg-[#0B3B2E]/5 flex items-center justify-center`}>
            {isPdf ? (
              <div className="flex flex-col items-center gap-2 text-[#14513D]">
                <FileText className="h-10 w-10" />
                <span className="text-xs max-w-[80%] truncate">{value.original_filename}</span>
                <span className="text-[10px] uppercase tracking-wider text-[#C9A34A]">PDF · first page used</span>
              </div>
            ) : (
              <img src={url} alt={label} className="h-full w-full object-contain" />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1 p-2 border-t border-[#C9A34A]/20">
            <Button size="sm" variant="ghost" onClick={pick} data-testid={`${testid}-replace`} className="gap-1 text-xs">
              <Replace className="h-3.5 w-3.5" /> Replace
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setFull(true)} data-testid={`${testid}-preview`} className="gap-1 text-xs">
              <Maximize2 className="h-3.5 w-3.5" /> Full view
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onChange(null)}
              data-testid={`${testid}-remove`}
              className="gap-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </Button>
          </div>
          {(lowRes || badAspect) && (
            <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 text-amber-800 text-xs border-t border-amber-200" data-testid={`${testid}-warning`}>
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                {lowRes && `Low resolution (${value.width}px wide, min ${minWidth}px). `}
                {badAspect && `This file is ${value.width} × ${value.height} px (${ratio.toFixed(2)}:1) — recommended ${aspectLabel}. ${aspectAdvice}`}
              </span>
            </div>
          )}
        </div>
      )}

      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0B3B2E]">{label || "Preview"}</DialogTitle>
          </DialogHeader>
          {isPdf ? (
            <iframe title="pdf" src={url} className="w-full h-[70vh] rounded-lg border border-[#C9A34A]/30" />
          ) : (
            <img src={url} alt="full" className="w-full max-h-[75vh] object-contain rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
